import { useMutation } from '@apollo/client'
import { SnackbarVariant } from '@focaldata/cin-ui-components'
import { useCallback } from 'react'
import { UPDATE_CUSTOM_CRITERION_OPTION_V2 } from '../../data/gql/questionnaire/mutations/criterionOptionV2'
import { draftQuestionnaireRefetchQuery } from '../../data/gql/questionnaire/queries'
import { draftAudienceRefetchQuery } from '../../data/gql/questionnaire/queries/audiences'
import { LoggerErrorType } from '../../data/logger'
import { captureApolloError } from '../../utils/HelperFunctions'
import {
  GLOBAL_AUDIENCE_READ_ONLY_MESSAGE,
  isGlobalAudienceReadOnlyError
} from '../../utils/globalAudienceError'
import { useOptionalWaveId } from '../useOptionalWaveId'
import { useProjectId } from '../useProjectId'
import { useSafeEnqueueSnackbar } from '../useSafeEnqueueSnackbar'
import { useSurveyId } from '../useSurveyId'

export const useUpdateCustomCriterionOptionV2 = () => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()
  const enqueueSnackbar = useSafeEnqueueSnackbar()

  const [updateCustomCriterionOptionV2] = useMutation(
    UPDATE_CUSTOM_CRITERION_OPTION_V2,
    {
      context: { clientName: 'questionnaire' },
      refetchQueries: [
        draftAudienceRefetchQuery(projectId, surveyId, waveId),
        draftQuestionnaireRefetchQuery(projectId, surveyId, waveId)
      ],
      onError(error) {
        captureApolloError(
          LoggerErrorType.ApolloMutation,
          'UpdateCustomCriterionOptionV2',
          error
        )
        // Backstop: the UI disables structural edits to a global question in a
        // non-base market, but surface the backend guard if one slips through.
        if (isGlobalAudienceReadOnlyError(error)) {
          enqueueSnackbar?.(GLOBAL_AUDIENCE_READ_ONLY_MESSAGE, {
            variant: SnackbarVariant.Error
          })
        }
      }
    }
  )

  return useCallback(
    (options: Parameters<typeof updateCustomCriterionOptionV2>[0]) =>
      updateCustomCriterionOptionV2({
        ...options,
        variables: options?.variables && {
          ...options.variables,
          input: { ...options.variables.input, waveId }
        }
      }),
    [updateCustomCriterionOptionV2, waveId]
  )
}
